import * as React from 'react'
import {
  faFileAlt,
  faPaperclip,
  faInfo
} from '@fortawesome/pro-light-svg-icons'

import { toCurrency } from '@espoon-voltti/ts-libs/lib/euro'
import { Column, Columns } from '@espoon-voltti/react-components/elements'

import { Section, Spacing } from '@espoon-voltti/react-components/layout'
import { LabelValueListItem } from '@espoon-voltti/react-components/components'
import { Collapsible } from '@espoon-voltti/react-voltti/components/collapsible'
import { AttachmentViewer } from '@espoon-voltti/react-voltti/components/attachment-selector'

import { GeneralGrantFormData } from '@espoon-voltti/nuora-domain/grants'
import { OrgApplicant } from '@espoon-voltti/nuora-domain/applicants'

import { t } from '../../../i18n'
import {
  attachmentApiUrl,
  attachmentAuthProfile
} from '../../../services/attachment-service'
import { sumOf } from '../../../utils'
import { OrgInfo, NuoraOrgInfo } from '../partials'

interface AppPreviewProps {
  form: GeneralGrantFormData
  applicant: OrgApplicant
}

export const GeneralGrantAppSigning: React.FC<AppPreviewProps> = (p) => {
  return (
    <>
      <Section>
        <Collapsible
          dataQa={'orgInfo'}
          title={t(['org', 'basicInfo', 'label'])}
          icon={faInfo}
          open={true}
        >
          <OrgInfo org={p.applicant.org} />
          <NuoraOrgInfo nuoraOrg={p.applicant.nuoraOrg} />
        </Collapsible>
      </Section>
      <Section>
        <Collapsible
          dataQa={'applicationDetails'}
          title={t(['views', 'generalGrant', 'preview', 'appDetails'])}
          icon={faFileAlt}
          open={true}
        >
          <LabelValueListItem
            dataQa={'detailsOfUse'}
            label={t(['views', 'generalGrant', 'preview', 'detailsOfUse'])}
            value={p.form.detailsOfUse}
          />
          <LabelValueListItem
            dataQa={'appliedSum'}
            label={t(['views', 'generalGrant', 'preview', 'appliedSum'])}
            value={toCurrency(p.form.appliedSum)}
          />

          <Spacing spacing={['margin-top--2']}>
            <div />
          </Spacing>

          <LabelValueListItem
            dataQa={'participants_from7to20'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'participants',
              'from7to20'
            ])}
            value={p.form.participants.from7to20}
          />
          <LabelValueListItem
            dataQa={'participants_from21to28'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'participants',
              'from21to28'
            ])}
            value={p.form.participants.from21to28}
          />
          <LabelValueListItem
            dataQa={'participantsTotal'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'participants',
              'total'
            ])}
            value={sumOf([
              p.form.participants.from7to20,
              p.form.participants.from21to28
            ])}
          />

          <Spacing spacing={['margin-top--2']}>
            <div />
          </Spacing>

          <LabelValueListItem
            dataQa={'goals1'}
            label={t(['views', 'generalGrant', 'preview', 'goals', 'goals_1'])}
            value={p.form.goals.goals1}
          />
          <LabelValueListItem
            dataQa={'goals2'}
            label={t(['views', 'generalGrant', 'preview', 'goals', 'goals_2'])}
            value={p.form.goals.goals2}
          />
          <LabelValueListItem
            dataQa={'goals3'}
            label={t(['views', 'generalGrant', 'preview', 'goals', 'goals_3'])}
            value={p.form.goals.goals3}
          />

          <Spacing spacing={['margin-top--2']}>
            <div />
          </Spacing>

          <LabelValueListItem
            dataQa={'youngEspoo'}
            label={t(['views', 'generalGrant', 'preview', 'youngEspoo'])}
            value={p.form.youngEspoo}
          />
          <LabelValueListItem
            dataQa={'financialPlan'}
            label={t(['views', 'generalGrant', 'preview', 'financialPlan'])}
            value={p.form.financialPlan}
          />
        </Collapsible>
      </Section>
      <Section>
        <Collapsible
          dataQa={'previousYear'}
          title={t([
            'views',
            'generalGrant',
            'preview',
            'previousYear',
            'title'
          ])}
          icon={faFileAlt}
          open={true}
        >
          <Spacing spacing={['margin-top--2']}>
            <div />
          </Spacing>
          <LabelValueListItem
            dataQa={'totalProfitInCents'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'previousYear',
              'totalProfitInCents'
            ])}
            value={toCurrency(p.form.previousYear.totalProfitInCents)}
          />
          <LabelValueListItem
            dataQa={'bankAccountSumInCents'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'previousYear',
              'bankAccountSumInCents'
            ])}
            value={toCurrency(p.form.previousYear.bankAccountSumInCents)}
          />
          <LabelValueListItem
            dataQa={'selfAcquiredFundsInCents'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'previousYear',
              'selfAcquiredFundsInCents'
            ])}
            value={toCurrency(p.form.previousYear.selfAcquiredFundsInCents)}
          />
          <LabelValueListItem
            dataQa={'receivedGrants'}
            label={t([
              'views',
              'generalGrant',
              'preview',
              'previousYear',
              'receivedGrants'
            ])}
            value={p.form.previousYear.receivedGrants}
          />
        </Collapsible>
      </Section>

      <Section dataQa="attachments">
        <Collapsible
          icon={faPaperclip}
          title={t(['views', 'preview', 'attachments', 'app', 'title'])}
        >
          <Columns>
            <Column desktopSize={6} tabletSize={12}>
              <AttachmentViewer
                attachmentApiUrl={attachmentApiUrl}
                authProfile={attachmentAuthProfile}
                models={p.form.attachments}
              />
            </Column>
          </Columns>
        </Collapsible>
      </Section>
    </>
  )
}
